import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useReducedMotion } from "framer-motion";

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  color: string;
  opacity: number;
  animationDuration: number;
  animationDelay: number;
  depth: number;
}

const DEFAULT_PARTICLE_COUNT = 12;
const MOBILE_PARTICLE_COUNT = 4;
const MOUSE_RADIUS = 150;
const REPULSION_STRENGTH = 30;

export function AnimatedBackground() {
  const prefersReducedMotion = useReducedMotion();
  const [mousePos, setMousePos] = useState({ x: -1000, y: -1000 });
  const [particleOffsets, setParticleOffsets] = useState<
    Record<number, { x: number; y: number }>
  >({});
  const containerRef = useRef<HTMLDivElement>(null);
  const throttleRef = useRef<number>(0);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Track small-screen / touch devices to reduce animation cost
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 640px), (pointer: coarse)");
    const update = () => setIsSmallScreen(!!mq.matches);
    update();
    const handleChange = () => update();
    if (mq.addEventListener) {
      mq.addEventListener("change", handleChange);
      return () => mq.removeEventListener("change", handleChange);
    } else {
      mq.addListener(handleChange);
      return () => mq.removeListener(handleChange);
    }
  }, []);

  // Generate particles; regenerate when screen size changes so we can create lighter particles on mobile
  const particles = useMemo<Particle[]>(() => {
    const colors = [
      "hsl(160 84% 39% / 0.6)", // primary - more visible
      "hsl(160 75% 45% / 0.5)", // primary lighter
      "hsl(160 60% 50% / 0.4)", // accent
      "hsl(142 76% 36% / 0.5)", // emerald
      "hsl(180 70% 40% / 0.4)", // teal
    ];

    const count = isSmallScreen
      ? MOBILE_PARTICLE_COUNT
      : DEFAULT_PARTICLE_COUNT;

    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: isSmallScreen ? 40 + Math.random() * 60 : 80 + Math.random() * 150,
      color: colors[Math.floor(Math.random() * colors.length)],
      opacity: isSmallScreen
        ? 0.35 + Math.random() * 0.35
        : 0.5 + Math.random() * 0.4,
      animationDuration: isSmallScreen
        ? 10 + Math.random() * 8
        : 25 + Math.random() * 35,
      animationDelay: Math.random() * -6,
      depth: Math.floor(Math.random() * 3), // 0 = back, 1 = mid, 2 = front
    }));
  }, [isSmallScreen]);

  // Throttled mouse move handler
  const handleMouseMove = useCallback((e: MouseEvent) => {
    const now = Date.now();
    if (now - throttleRef.current < 50) return; // 50ms throttle
    throttleRef.current = now;

    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  }, []);

  // Cache container dimensions
  const dimensionsRef = useRef({ width: 0, height: 0 });
  const rafRef = useRef<number>();

  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        dimensionsRef.current = { width: rect.width, height: rect.height };
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions, { passive: true });

    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  // Calculate particle offsets based on mouse proximity
  useEffect(() => {
    if (prefersReducedMotion || !containerRef.current) return;

    // Cancel any pending RAF
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      const { width, height } = dimensionsRef.current;
      const newOffsets: Record<number, { x: number; y: number }> = {};

      particles.forEach((particle) => {
        const particleX = (particle.x / 100) * width;
        const particleY = (particle.y / 100) * height;

        const dx = particleX - mousePos.x;
        const dy = particleY - mousePos.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < MOUSE_RADIUS && distance > 0) {
          const force = (1 - distance / MOUSE_RADIUS) * REPULSION_STRENGTH;
          const depthMultiplier = 1 + particle.depth * 0.3;
          newOffsets[particle.id] = {
            x: (dx / distance) * force * depthMultiplier,
            y: (dy / distance) * force * depthMultiplier,
          };
        } else {
          newOffsets[particle.id] = { x: 0, y: 0 };
        }
      });

      setParticleOffsets(newOffsets);
    });

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [mousePos, particles, prefersReducedMotion]);

  // Set up mouse listener (disabled on small screens or when motion is reduced)
  useEffect(() => {
    if (prefersReducedMotion || isSmallScreen) return;

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [handleMouseMove, prefersReducedMotion, isSmallScreen]);

  // Static gradient fallback for reduced motion
  if (prefersReducedMotion) {
    return (
      <div
        className="fixed inset-0 pointer-events-none z-0"
        aria-hidden="true"
        style={{
          background: `
            radial-gradient(ellipse at 20% 30%, hsl(var(--primary) / 0.12) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 70%, hsl(var(--accent) / 0.1) 0%, transparent 50%)
          `,
        }}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 pointer-events-none z-0 overflow-hidden"
      aria-hidden="true"
      style={{ filter: "blur(3px)" }}
    >
      {/* Base gradient layer */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse at 20% 30%, hsl(var(--primary) / 0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 70%, hsl(var(--accent) / 0.06) 0%, transparent 50%)
          `,
        }}
      />

      {/* Particles */}
      {particles.map((particle) => {
        const offset = particleOffsets[particle.id] || { x: 0, y: 0 };
        const depthScale = 0.7 + particle.depth * 0.15;
        const depthBlur = 20 + (2 - particle.depth) * 15; // Much less blur for visibility

        // Outer container handles positioning + mouse offset (so it won't be overwritten by keyframe animations)
        return (
          <div
            key={particle.id}
            className="absolute rounded-full will-change-transform"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: particle.size * depthScale,
              height: particle.size * depthScale,
              opacity:
                particle.opacity * depthScale * (isSmallScreen ? 0.9 : 1),
              transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px)`,
              transition: isSmallScreen
                ? "transform 0.12s linear"
                : "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            {/* Inner element manages floating animation so mouse offsets stay applied consistently */}
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "9999px",
                background: `radial-gradient(circle at 30% 30%, ${particle.color}, transparent 60%)`,
                willChange: "transform",
                transformOrigin: "50% 50%",
                animation: `float-${particle.depth} ${particle.animationDuration}s cubic-bezier(0.22, 1, 0.36, 1) infinite alternate`,
                animationDelay: `${particle.animationDelay}s`,
              }}
            />
          </div>
        );
      })}

      {/* CSS Keyframes for floating animation (softened amplitudes and smoother easing) */}
      <style>{`
        @keyframes float-0 {
          0% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
          25% { transform: translate3d(12px, -9px, 0) rotate(1deg) scale(1.01); }
          50% { transform: translate3d(-6px, 12px, 0) rotate(-0.5deg) scale(0.995); }
          75% { transform: translate3d(-15px, -6px, 0) rotate(0.7deg) scale(1); }
          100% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
        }
        @keyframes float-1 {
          0% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
          33% { transform: translate3d(-18px, 12px, 0) rotate(-1deg) scale(0.998); }
          66% { transform: translate3d(14px, -14px, 0) rotate(1.2deg) scale(1.01); }
          100% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
        }
        @keyframes float-2 {
          0% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
          20% { transform: translate3d(9px, 15px, 0) rotate(0.6deg) scale(1.005); }
          40% { transform: translate3d(-12px, -6px, 0) rotate(-0.6deg) scale(0.997); }
          60% { transform: translate3d(15px, -12px, 0) rotate(1deg) scale(1.002); }
          80% { transform: translate3d(-9px, 9px, 0) rotate(-0.8deg) scale(0.999); }
          100% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
        }
      `}</style>
    </div>
  );
}
